import React from 'react';
import { PaymentMethod } from '@models/subscription';

export interface PaymentMethodListProps {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodId: string | null;
  onSelectPaymentMethod: (id: string) => void;
  showAddNewOption?: boolean;
  isAddingNew?: boolean;
  onAddNew?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const CreditCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
    />
  </svg>
);

const formatBrand = (brand?: string): string => {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
};

const PaymentMethodList: React.FC<PaymentMethodListProps> = ({
  paymentMethods,
  selectedPaymentMethodId,
  onSelectPaymentMethod,
  showAddNewOption = true,
  isAddingNew = false,
  onAddNew,
  isLoading = false,
  disabled = false,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Payment Method
        </label>
        <div className="text-center py-4 text-gray-500">Loading payment methods...</div>
      </div>
    );
  }

  const handleSelectExisting = (paymentMethodId: string) => {
    if (disabled) return;
    onSelectPaymentMethod(paymentMethodId);
  };

  const handleAddNew = () => {
    if (disabled || !onAddNew) return;
    onAddNew();
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Payment Method
      </label>
      <div className="space-y-2">
        {/* Existing payment methods */}
        {paymentMethods.map((paymentMethod) => {
          const isSelected = !isAddingNew && selectedPaymentMethodId === paymentMethod.paymentMethodId;
          return (
            <label
              key={paymentMethod.paymentMethodId}
              className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => handleSelectExisting(paymentMethod.paymentMethodId)}>
              <input
                type="radio"
                name="paymentMethodChoice"
                checked={isSelected}
                onChange={() => handleSelectExisting(paymentMethod.paymentMethodId)}
                disabled={disabled}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div className="ml-3 flex items-center gap-2">
                <CreditCardIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatBrand(paymentMethod.brand)} ending in{' '}
                  <span className="font-mono">{paymentMethod.last4}</span>
                </span>
                {paymentMethod.expiryMonth && paymentMethod.expiryYear && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    (Exp: {paymentMethod.expiryMonth}/{paymentMethod.expiryYear})
                  </span>
                )}
                {paymentMethod.isDefault && (
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
                    Default
                  </span>
                )}
              </div>
            </label>
          );
        })}

        {/* Add new card option */}
        {showAddNewOption && onAddNew && (
          <label
            className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
              isAddingNew
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleAddNew}>
            <input
              type="radio"
              name="paymentMethodChoice"
              checked={isAddingNew}
              onChange={handleAddNew}
              disabled={disabled}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <div className="ml-3 flex items-center gap-2">
              <PlusIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Add a new card
              </span>
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodList;
